import {Http} from './utils';

/*
 * Warehouse availability on the search page (ITC-725).
 *
 * When /search finds nothing in our own inventory we run a deeper search. The
 * backend answers immediately with whatever the indexed sources returned plus a
 * request_id; the sources that have to be queried live keep running in the
 * background, and we poll the request_id for them.
 *
 * Three states below an empty result, exactly one visible at a time:
 *
 *   searching  the template's spinner — nothing to show yet, still looking
 *   card       something was found; a loader stays on it while sources are out
 *   banner     the "not found" request-for-quote block
 *
 * The banner is never the first thing a visitor sees: the template renders it
 * hidden and it only appears once the deeper search has actually come back
 * empty (or could not run at all).
 */

const START_URL = '/warehouse-availability';

/*
 * How long we keep asking. Nobody waits on a search page for longer than this —
 * past it the card keeps whatever it has and stops promising more.
 */
const POLL_TIMEOUT_MS = 60000;

/*
 * Polling cadence. Nearly every lookup settles inside the first few seconds, so
 * the first POLL_STEADY_POLLS go out at the base interval; past that we are
 * waiting on a slow upstream and there is no point asking at the same rate, so
 * the gap grows geometrically up to a ceiling. Over the full window that is
 * about ten requests instead of twenty-four.
 */
const POLL_INTERVAL_MS = 2500;
const POLL_STEADY_POLLS = 5;
const POLL_BACKOFF_FACTOR = 1.6;
const POLL_MAX_INTERVAL_MS = 15000;

/*
 * Consecutive failed polls before we stop asking. A successful poll resets the
 * count, so this is "it is really broken", not "one response was lost".
 */
const MAX_POLL_FAILURES = 2;
const STATUS_FINISHED = 'finished';

/**
 * How long to wait before poll number `polled + 1`.
 *
 * @param polled how many polls have already gone out
 * @returns {number}
 */
function nextDelay(polled) {
    if (polled < POLL_STEADY_POLLS) {
        return POLL_INTERVAL_MS;
    }

    const grown = POLL_INTERVAL_MS * POLL_BACKOFF_FACTOR ** (polled - POLL_STEADY_POLLS + 1);

    return Math.min(Math.round(grown), POLL_MAX_INTERVAL_MS);
}

/*
 * Cheap pre-check so an ordinary text search ("dell server memory") does not cost
 * a round trip just to be told 422. Deliberately looser than the server rule in
 * config/catalog.php — it checks length, a digit and the word count but not the
 * character set, so it can only reject what the server would reject too. The
 * server stays the single authority on what a part number is.
 *
 * Up to two words, because a bare `+` in a query string decodes to a space and
 * SKUs like RX7800XT+16G-L/OC then arrive as `RX7800XT 16G-L/OC`.
 */
const MIN_LENGTH = 3;
const MAX_LENGTH = 50;
const MAX_WORDS = 2;

function looksLikePartNumber(value) {
    return value.length >= MIN_LENGTH
        && value.length <= MAX_LENGTH
        && /\d/.test(value)
        && value.split(/\s+/).length <= MAX_WORDS;
}

const escapeHtml = (value) => {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);

    return div.innerHTML;
};
const escapeAttr = (value) => escapeHtml(value).replace(/"/g, '&quot;');

const quoteIcon = '<img src="/build/assets/svg/icons/quote.svg" height="30" width="25" alt="Quote icon"/>';

/**
 * Mirrors components/add-quote-button.twig — the basket picks the row up from
 * these data attributes, no request involved.
 *
 * @param product
 * @param partNumber
 * @returns {string}
 */
function quoteButton(product, partNumber) {
    const partnum = product.partnum || partNumber;

    return `
        <button type="button" class="btn-quote add-to-quote-btn"
            data-id="wh-${escapeAttr(partnum)}"
            data-partnum="${escapeAttr(partnum)}"
            data-name="${escapeAttr(product.productName || partnum)}"
            data-manufacturer="${escapeAttr(product.manufacturer || '')}"
            data-condition="${escapeAttr(product.condition || '')}"
            data-type="warehouse"
            onclick="addToQuote(this)">
            ${quoteIcon}
            Add to Quote
        </button>
    `;
}

/**
 * @param payload
 * @returns {string}
 */
function cardHtml(payload) {
    const product = payload.product || {};
    const partNumber = payload.partNumber || '';
    const title = product.productName || `Part number ${partNumber}`;

    const rows = [
        ['Part number', product.partnum || partNumber],
        ['Condition', product.condition],
        ['Manufacturer', product.manufacturer],
        ['Category', product.category],
    ]
        .filter(([, value]) => Boolean(value))
        .map(([label, value]) => `<p><b>${label}:</b> ${escapeHtml(value)}</p>`)
        .join('');

    return `
        <p class="warehouse-intro">
            Please add the part to your quote request and we will respond with a proposal.
        </p>
        <div class="item-row">
            <div class="item-bottom items-list no-image">
                <div class="data-box">
                    <span class="conf-title prod-desc">${escapeHtml(title)}</span>
                    <div class="data-row">
                        <div class="description-box">${rows}</div>

                        <div class="price-box">
                            <div class="warehouse-availability" data-availability hidden>
                                <span class="warehouse-availability-count" data-availability-count hidden>
                                    <span class="warehouse-availability-qty" data-availability-qty>0</span>
                                    <span class="warehouse-availability-label">units available</span>
                                </span>
                                <span class="warehouse-availability-loader" data-availability-loader hidden>
                                    <progress class="mat-progress s18"></progress>
                                    <span class="warehouse-availability-hint" data-availability-hint></span>
                                </span>
                            </div>
                            <div class="btn-box">${quoteButton(product, partNumber)}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `;
}

/**
 * Numbers and loader change on every poll; the card markup itself is only
 * rebuilt when the part data finally arrives from a slower source.
 *
 * @param box
 * @param payload
 */
function paint(box, payload) {
    const availability = box.querySelector('[data-availability]');
    const count = box.querySelector('[data-availability-count]');
    const qty = box.querySelector('[data-availability-qty]');
    const loader = box.querySelector('[data-availability-loader]');
    const hint = box.querySelector('[data-availability-hint]');
    const finished = payload.status === STATUS_FINISHED;
    const total = payload.totalAvailability || 0;

    if (qty) {
        qty.textContent = String(total);
    }

    // "0 units available" says nothing a visitor can use and reads as a dead end
    // next to the quote button — show the number only when there is stock.
    if (count) {
        count.hidden = total <= 0;
    }

    // `status` is the only progress signal the payload carries — on purpose, a
    // count of sources would say how many places we can ask.
    if (hint) {
        hint.textContent = finished ? '' : 'still checking…';
    }

    if (loader) {
        loader.hidden = finished;
    }

    // Both children gone: drop the row itself so it leaves no gap.
    if (availability) {
        availability.hidden = total <= 0 && finished;
    }

    box.classList.toggle('is-loading', !finished);
}

/**
 * Give up on the deeper search and show the request-for-quote banner.
 *
 * @param els
 */
function showBanner(els) {
    els.box.hidden = true;
    els.box.innerHTML = '';

    if (els.searchingBox) {
        els.searchingBox.hidden = true;
    }

    if (els.noResultBox) {
        els.noResultBox.hidden = false;
    }
}

/**
 * @param els
 * @param payload
 * @param state
 */
function render(els, payload, state) {
    const hasProduct = Boolean(payload.product);
    const hasData = hasProduct || (payload.totalAvailability || 0) > 0;
    const finished = payload.status === STATUS_FINISHED;

    if (!hasData) {
        // Nothing found and nothing left to look at — this is the one case the
        // banner is for. Otherwise keep the spinner: an empty card would be
        // worse than "still searching".
        if (finished) {
            showBanner(els);
        }

        return;
    }

    const {box} = els;

    if (!state.rendered || (hasProduct && !state.hasProduct)) {
        box.innerHTML = cardHtml(payload);
        state.rendered = true;
        state.hasProduct = hasProduct;
    }

    paint(box, payload);

    box.hidden = false;

    if (els.searchingBox) {
        els.searchingBox.hidden = true;
    }

    if (els.noResultBox) {
        els.noResultBox.hidden = true;
    }
}

/**
 * End the loop without an answer: keep whatever is already on the card, or fall
 * back to the banner if the card was never rendered.
 *
 * @param els
 * @param state
 */
function giveUp(els, state) {
    if (!state.rendered) {
        // Nothing ever reached the screen, so the spinner has to resolve into
        // something the visitor can act on.
        showBanner(els);

        return;
    }

    els.box.classList.remove('is-loading');

    const loader = els.box.querySelector('[data-availability-loader]');
    if (loader) {
        loader.hidden = true;
    }
}

/**
 * @param els
 * @param requestId
 * @param state
 */
function poll(els, requestId, state) {
    const startedAt = Date.now();
    const remaining = () => POLL_TIMEOUT_MS - (Date.now() - startedAt);
    const expired = () => remaining() <= 0;
    const schedule = () => {
        // Never schedule past the deadline: without the clamp the backoff would
        // overshoot it by up to a full interval, and the last poll would land
        // seconds after we promised to stop.
        state.timer = window.setTimeout(tick, Math.min(nextDelay(state.polls), remaining()));
    };

    const tick = () => {
        state.polls += 1;

        Http.getJson(`${START_URL}/${encodeURIComponent(requestId)}`)
            .then(payload => {
                if (!payload) {
                    // A 200 with nothing in it is as useless as an error, and
                    // returning here without rescheduling used to freeze the
                    // spinner forever.
                    throw new Error('empty poll response');
                }

                state.failures = 0;

                render(els, payload, state);

                if (payload.status === STATUS_FINISHED) {
                    return;
                }

                if (expired()) {
                    // Give up quietly: whatever is on screen stays, it just stops
                    // claiming that more is on the way.
                    render(els, {...payload, status: STATUS_FINISHED}, state);

                    return;
                }

                schedule();
            })
            .catch(() => {
                // A 404 means the record expired; anything else is usually a
                // blip. Either way, keep trying until the limit — a lookup that
                // is still running should survive one bad response.
                state.failures += 1;

                if (state.failures >= MAX_POLL_FAILURES || expired()) {
                    giveUp(els, state);

                    return;
                }

                schedule();
            });
    };

    schedule();
}

/**
 * Entry point — no-ops unless the page rendered the placeholder, which only
 * happens when /search returned nothing.
 */
export function initWarehouseAvailability() {
    const box = document.getElementById('warehouse-availability-box');

    if (!box) {
        return;
    }

    const els = {
        box,
        searchingBox: document.getElementById('warehouse-availability-searching'),
        noResultBox: document.getElementById('search-no-result'),
    };
    const partNumber = (box.dataset.partnum || '').trim();

    if (!looksLikePartNumber(partNumber)) {
        // The template normally never renders the spinner in this case; if it
        // did, resolve it rather than leaving it turning.
        showBanner(els);

        return;
    }

    const state = {rendered: false, hasProduct: false, timer: null, failures: 0, polls: 0};

    Http.getJson(`${START_URL}?partnumber=${encodeURIComponent(partNumber)}`)
        .then(payload => {
            if (!payload || !payload.requestId) {
                showBanner(els);

                return;
            }

            render(els, payload, state);

            if (payload.status !== STATUS_FINISHED) {
                poll(els, payload.requestId, state);
            }
        })
        .catch(() => {
            // 422 (the server's stricter rule rejected the phrase) or the lookup
            // is down — either way there is nothing coming, so stop the spinner
            // and give the visitor the quote form.
            showBanner(els);
        });
}
